import { useEffect, useState } from 'react';

import { useQuery } from '@tanstack/react-query';

import { BASED_ON_RECENT_VIEWS_LIMIT } from '../constants';

import { RQ_BASED_ON_RECENT_VIEWS } from '@/modules/ReactQuery/cacheKeys';
import { getPersistentId } from '@/modules/activityTracker/helpers';
import {
  fetchBasedOnRecentViewsAuth,
  fetchBasedOnRecentViewsUnauth,
} from '@/modules/basedOnRecentViews/api';
import { getLocallyStoredRecentlyViewedProducts } from '@/modules/recentlyViewed/helpers';
import { useCurrentUser } from '@/modules/user/useCurrentUser';

export function useBasedOnRecentViews({
  limit = BASED_ON_RECENT_VIEWS_LIMIT,
}: { limit?: number } = {}) {
  const [isAuthenticated] = useCurrentUser();
  const [localProductIds, setLocalProductIds] = useState<number[]>([]);
  const [deviceId, setDeviceId] = useState<string | undefined>();

  useEffect(() => {
    if (!isAuthenticated) {
      const storedProducts = getLocallyStoredRecentlyViewedProducts();
      setLocalProductIds(storedProducts.map((product) => product.id));
    }
  }, [isAuthenticated]);

  useEffect(() => {
    getPersistentId().then((id) => setDeviceId(id || undefined));
  }, []);

  const hasLocalProducts = localProductIds.length > 0;

  const query = useQuery({
    queryKey: [RQ_BASED_ON_RECENT_VIEWS, { isAuthenticated, localProductIds }],
    queryFn: async () => {
      if (isAuthenticated) {
        const response = await fetchBasedOnRecentViewsAuth({ limit });
        return response.data.objects;
      }

      const response = await fetchBasedOnRecentViewsUnauth({
        productIds: localProductIds,
        limit,
      });
      return response.data.objects;
    },
    enabled: !!deviceId && (isAuthenticated || hasLocalProducts),
  });

  return {
    products: query.data ?? [],
    isLoading: query.isLoading,
  };
}
